import { TAP_SPRITE_EMOJI } from '../../effects/tapSprites';
import { buildBlankCustomTheme } from '../../themes/customThemeDefaults';
import { CUSTOM_THEME_KEY } from '../../themes/registry';
import type { ThemeRecipe } from '../../themes/recipe';
import type { CustomTheme, TapSprite } from '../../themes/types';
import { uid } from '../uid';

// Schema migration runner. Every persisted document carries a
// `schemaVersion`; on read, this brings the doc up to current via a chain of
// deterministic per-version migrations. See plan/02 §Schema versioning and
// plan/06 D6.

export const CURRENT_SCHEMA_VERSION = 5;

export interface VersionedDoc {
  schemaVersion: number;
}

// A migration upgrades a doc from version N to N+1. Pure and deterministic.
// Must set `schemaVersion = N + 1` on its output.
export type Migration = (doc: VersionedDoc) => VersionedDoc;

// Keyed by source version: `migrations[1]` upgrades v1 → v2.
export type MigrationMap = Record<number, Migration>;

// v1 → v2: per tasks/v2-themes.md §D4, replace `profile.themeKey: string`
// with `profile.theme: { default: themeKey }`. Per-routine overrides
// remain absent until the picker UI lands; the field is optional.
//
// Refuses to silently coerce a non-array `profiles` field — that would
// drop user data on a corrupt v1 doc. Missing/null is treated as empty
// (already the v1 "no profiles" representation).
const v1_to_v2: Migration = (doc) => {
  const tree = doc as VersionedDoc & { profiles?: unknown };
  const raw = tree.profiles;
  if (raw !== undefined && raw !== null && !Array.isArray(raw)) {
    throw new Error('v1 → v2 migration: `profiles` is present but not an array');
  }
  const list = (raw ?? []) as Array<Record<string, unknown>>;
  const profiles = list.map((profile) => {
    const { themeKey, ...rest } = profile as Record<string, unknown> & {
      themeKey?: string;
    };
    return {
      ...rest,
      theme: { default: typeof themeKey === 'string' ? themeKey : 'sky' },
    };
  });
  return { ...tree, profiles, schemaVersion: 2 };
};

// v2 → v3: per plan tasks/43 §1, hoist `profile.routines: Routine[]` into a
// top-level `tree.routines: Routine[]` library and give each profile an
// ordered `profile.routineIds: Id[]` reference list. Preserves the
// per-profile display order via `routineIds`; the routine's `sortIndex`
// stays untouched (used as a library-wide fallback).
//
// v2 had no shared routine ids (each profile owned its own set), so a
// straight lift preserves identity — no id collisions. Where a corrupt v2
// doc somehow contains an id shared across profiles, keep the entry with
// the newer `updatedAt`; that mirrors v1 → v2's defensive stance and the
// conflict-merge tiebreaker in features/sync/conflictMerge.
//
// Refuses to silently coerce: a non-array `profiles` or a profile with a
// non-array `routines` throws instead of dropping user data.
const v2_to_v3: Migration = (doc) => {
  const tree = doc as VersionedDoc & { profiles?: unknown; routines?: unknown };
  const raw = tree.profiles;
  if (raw !== undefined && raw !== null && !Array.isArray(raw)) {
    throw new Error('v2 → v3 migration: `profiles` is present but not an array');
  }
  const list = (raw ?? []) as Array<Record<string, unknown>>;

  // Library is keyed by routine id; later profiles with a colliding id
  // overwrite iff their routine's `updatedAt` is strictly newer.
  const library = new Map<string, Record<string, unknown>>();
  const migratedProfiles = list.map((profile) => {
    const { routines, ...rest } = profile as Record<string, unknown> & {
      routines?: unknown;
    };
    if (routines !== undefined && routines !== null && !Array.isArray(routines)) {
      throw new Error(
        `v2 → v3 migration: profile ${String((profile as { id?: unknown }).id)} has non-array \`routines\``,
      );
    }
    const routineList = (routines ?? []) as Array<Record<string, unknown>>;
    // Preserve the profile's order via routineIds.
    const routineIds: string[] = [];
    for (const routine of routineList) {
      const id = routine.id;
      if (typeof id !== 'string' || id.length === 0) {
        throw new Error(
          `v2 → v3 migration: profile ${String((profile as { id?: unknown }).id)} has a routine without a string id`,
        );
      }
      routineIds.push(id);
      const existing = library.get(id);
      if (!existing) {
        library.set(id, routine);
        continue;
      }
      const existingUpdated = typeof existing.updatedAt === 'string' ? existing.updatedAt : '';
      const nextUpdated = typeof routine.updatedAt === 'string' ? routine.updatedAt : '';
      if (nextUpdated > existingUpdated) library.set(id, routine);
    }
    return { ...rest, routineIds };
  });

  return {
    ...tree,
    profiles: migratedProfiles,
    routines: Array.from(library.values()),
    schemaVersion: 3,
  };
};

// v3 → v4: per tasks/46 §D6, promote each profile's one-shot inline
// `theme.custom: ThemeRecipe` into a family-shared library entry keyed
// by id. `SyncedTree.customThemes: CustomTheme[]` is seeded (empty by
// default); rewrites `theme.default` and any `theme.perRoutine[*]` value
// still pointing at the sentinel `CUSTOM_THEME_KEY` to the new library
// id; deletes the legacy inline recipe. New effect specs
// (`tapEffect` / `celebration` / `sound`) are populated from
// `customThemeDefaults` so the migrated shape is indistinguishable from
// a fresh #47 create.
//
// Refuses to silently coerce a pre-existing but non-array `customThemes`
// field (matches the v1 → v2 defensive stance).
interface V3Theme {
  default?: unknown;
  perRoutine?: Record<string, unknown>;
  custom?: unknown;
}

interface V3ProfileTheme {
  usedAsDefault: boolean;
  perRoutineHits: string[];
  newDefault: string;
  nextPerRoutine: Record<string, string>;
}

// Scan the profile's v3 theme block for references to the legacy sentinel
// and produce the base v4 theme shape (still without the library id
// substitution, which the caller does once it knows the id).
function analyseV3Theme(theme: V3Theme): V3ProfileTheme {
  const usedAsDefault = theme.default === CUSTOM_THEME_KEY;
  const perRoutineHits: string[] = [];
  const nextPerRoutine: Record<string, string> = {};
  if (theme.perRoutine) {
    for (const [routineId, value] of Object.entries(theme.perRoutine)) {
      if (value === CUSTOM_THEME_KEY) perRoutineHits.push(routineId);
      if (typeof value === 'string') nextPerRoutine[routineId] = value;
    }
  }
  return {
    usedAsDefault,
    perRoutineHits,
    newDefault: typeof theme.default === 'string' ? theme.default : 'sky',
    nextPerRoutine,
  };
}

function migrateProfileV3ToV4(
  profile: Record<string, unknown>,
  customThemes: CustomTheme[],
): Record<string, unknown> {
  const theme = (profile.theme ?? {}) as V3Theme;
  const legacyRecipe = isThemeRecipe(theme.custom) ? theme.custom : null;
  const analysed = analyseV3Theme(theme);

  // Only synthesise a library entry when the legacy recipe was actually
  // referenced. Dangling recipes with no reference are dropped — they've
  // never been visible to the user.
  const legacyReferenced =
    legacyRecipe !== null && (analysed.usedAsDefault || analysed.perRoutineHits.length > 0);
  let { newDefault } = analysed;
  const { nextPerRoutine } = analysed;
  if (legacyReferenced && legacyRecipe) {
    const profileName = typeof profile.name === 'string' ? profile.name : 'Oma';
    const custom = buildBlankCustomTheme(uid(), `${profileName} — Oma`, timestamp(profile), {
      recipe: legacyRecipe,
    });
    customThemes.push(custom);
    if (analysed.usedAsDefault) newDefault = custom.id;
    for (const routineId of analysed.perRoutineHits) nextPerRoutine[routineId] = custom.id;
  }

  const nextTheme: Record<string, unknown> = { default: newDefault };
  if (Object.keys(nextPerRoutine).length > 0) nextTheme.perRoutine = nextPerRoutine;
  // The legacy `custom` recipe is intentionally omitted — it lives in
  // the library now.
  return { ...withoutKey(profile, 'theme'), theme: nextTheme };
}

function withoutKey(record: Record<string, unknown>, key: string): Record<string, unknown> {
  const clone = { ...record };
  delete clone[key];
  return clone;
}

const v3_to_v4: Migration = (doc) => {
  const tree = doc as VersionedDoc & { profiles?: unknown; customThemes?: unknown };
  const existing = tree.customThemes;
  if (existing !== undefined && existing !== null && !Array.isArray(existing)) {
    throw new Error('v3 → v4 migration: `customThemes` is present but not an array');
  }
  const customThemes: CustomTheme[] = ((existing ?? []) as CustomTheme[]).slice();

  const raw = tree.profiles;
  if (raw !== undefined && raw !== null && !Array.isArray(raw)) {
    throw new Error('v3 → v4 migration: `profiles` is present but not an array');
  }
  const list = (raw ?? []) as Array<Record<string, unknown>>;

  const migratedProfiles = list.map((profile) => migrateProfileV3ToV4(profile, customThemes));

  return {
    ...tree,
    profiles: migratedProfiles,
    customThemes,
    schemaVersion: 4,
  };
};

// v3 stored the recipe under `profile.theme.custom`. Guard the migration
// against corrupt shapes (non-object, missing `base`) so we don't crash
// mid-boot on a hand-tampered tree.
function isThemeRecipe(value: unknown): value is ThemeRecipe {
  if (!value || typeof value !== 'object') return false;
  const rec = value as Record<string, unknown>;
  return typeof rec.base === 'string';
}

function timestamp(profile: Record<string, unknown>): string {
  const created = profile.createdAt;
  if (typeof created === 'string' && created.length > 0) return created;
  return new Date().toISOString();
}

// v4 → v5: per tasks/51, rewrite each custom theme's `tapEffect` variants
// so a `fly-to-counter` variant carries an `emojis: string[]` list (1–5
// emojis) instead of a single `sprite: TapSprite` slot. Existing sprites
// become a single-element list via `TAP_SPRITE_EMOJI[sprite]`; a
// spriteless variant loses the field (runtime falls back to reward emoji).
// `sparkle-burst` variants are untouched — their `sprite` was inert.
//
// Refuses to silently coerce a non-array `customThemes` or `variants`.
const v4_to_v5: Migration = (doc) => {
  const tree = doc as VersionedDoc & { customThemes?: unknown };
  const raw = tree.customThemes;
  if (raw !== undefined && raw !== null && !Array.isArray(raw)) {
    throw new Error('v4 → v5 migration: `customThemes` is present but not an array');
  }
  const list = (raw ?? []) as Array<Record<string, unknown>>;
  const migrated = list.map(migrateCustomThemeV4ToV5);
  return { ...tree, customThemes: migrated, schemaVersion: 5 };
};

function migrateCustomThemeV4ToV5(theme: Record<string, unknown>): Record<string, unknown> {
  const tapEffect = theme.tapEffect as Record<string, unknown> | undefined;
  if (!tapEffect) return theme;
  const variantsRaw = tapEffect.variants;
  if (variantsRaw !== undefined && variantsRaw !== null && !Array.isArray(variantsRaw)) {
    throw new Error(
      `v4 → v5 migration: custom theme ${String(theme.id)} has non-array \`tapEffect.variants\``,
    );
  }
  const variants = (variantsRaw ?? []) as Array<Record<string, unknown>>;
  const nextVariants = variants.map(migrateTapVariantV4ToV5);
  return { ...theme, tapEffect: { ...tapEffect, variants: nextVariants } };
}

function migrateTapVariantV4ToV5(variant: Record<string, unknown>): Record<string, unknown> {
  if (variant.kind !== 'fly-to-counter') return variant;
  const { sprite, ...rest } = variant as { sprite?: unknown } & Record<string, unknown>;
  if (typeof sprite === 'string' && sprite in TAP_SPRITE_EMOJI) {
    return { ...rest, emojis: [TAP_SPRITE_EMOJI[sprite as TapSprite]] };
  }
  return rest;
}

const migrations: MigrationMap = {
  1: v1_to_v2,
  2: v2_to_v3,
  3: v3_to_v4,
  4: v4_to_v5,
};

export class SchemaVersionError extends Error {
  constructor(
    public readonly docVersion: number,
    public readonly currentVersion: number,
  ) {
    super(
      `Doc schemaVersion ${docVersion} is newer than current ${currentVersion}; refuse to overwrite.`,
    );
    this.name = 'SchemaVersionError';
  }
}

// Run the chain of migrations from `doc.schemaVersion` up to `target`.
// Throws SchemaVersionError if the doc is newer than target — see plan/02:
// "If newer → log a warning, refuse to overwrite, prompt the user to update".
export function runMigrations<T extends VersionedDoc>(
  doc: T,
  target: number = CURRENT_SCHEMA_VERSION,
  registry: MigrationMap = migrations,
): T {
  const v: unknown = doc.schemaVersion;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) {
    throw new Error(`Invalid schemaVersion: ${String(v)}; expected positive integer.`);
  }
  if (v > target) {
    throw new SchemaVersionError(v, target);
  }
  let result: VersionedDoc = doc;
  while (result.schemaVersion < target) {
    const from = result.schemaVersion;
    const upgrade = registry[from];
    if (!upgrade) {
      throw new Error(`Missing migration for v${from} → v${from + 1}`);
    }
    const next = upgrade(result);
    if (next.schemaVersion !== from + 1) {
      throw new Error(
        `Migration v${from} → v${from + 1} produced schemaVersion ${next.schemaVersion}`,
      );
    }
    result = next;
  }
  return result as T;
}
